import { Injectable } from '@angular/core';
import { BaseApiService } from './base-api-service';
import { BaseEntityService } from './base-entity';
import { StorageService } from './storage';

export interface Product {
  id: number;
  templateId: number;
  categoryId: number;
  name: string;
  default_code: string;
  mrp: number;
  minimum_price: number;
  list_price: number;
  qty_available: number;
  image: string;
}

export interface SelectedProduct {
  productId: number,
  quantity: number,
  price: number
}

/**
 * Applies the internally configured sale-price floor without exposing it in the UI.
 * This is used for pricelist, customer last/favorite price, manual edit and pending-order flows.
 */
export function normalizeSalePrice(
  product: Product | undefined,
  requestedPrice: number | null | undefined
): number {
  const parsedPrice = Number(requestedPrice ?? 0);
  const price = Number.isFinite(parsedPrice) ? parsedPrice : 0;

  const parsedFloor = Number(product?.minimum_price ?? 0);
  const floor = Number.isFinite(parsedFloor) && parsedFloor > 0 ? parsedFloor : 0;

  return Math.max(price, floor);
}

/**
 * Returns sale lines with prices silently adjusted to the allowed internal floor.
 * Unknown products are left with their requested price so unrelated/offline data is preserved.
 */
export function normalizeSaleItems(
  items: SelectedProduct[],
  products: Product[]
): SelectedProduct[] {
  const productMap = new Map<number, Product>(products.map(product => [product.id, product]));

  return (items || []).map(item => {
    const product = productMap.get(item.productId);
    if (!product) {
      return item;
    }

    const price = normalizeSalePrice(product, item.price);
    return price === item.price ? item : { ...item, price };
  });
}

@Injectable({
  providedIn: 'root'
})
export class ProductService extends BaseEntityService<Product> {
  constructor(
    indexedDbService: StorageService<Product>,
    baseApi: BaseApiService
  ) {
    super(
      baseApi,
      indexedDbService,
      'ProductDatabase',  // IndexedDB database name
      'products',         // Object store name
      'products'          // API endpoint path
    );
  }
}
